import { Light, LightFields, LightProperties } from '../../core/Light';
import { Vector3, Euler } from 'three';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { colors } from '@/constants/theme';
import { Field } from '@/types/core/Field';

export interface SpotLightProperties extends LightProperties {
  intensity: number;
  color: string;
  angle: number;
  penumbra: number;
  distance: number;
  decay: number;
}

// Create a reusable Spot Light component that can be used in both preview and world
const SpotLightComponent = forwardRef<THREE.SpotLight, { properties: SpotLightProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { intensity, color, angle, penumbra, distance, decay, transform } = properties;
  const localRef = useRef<THREE.SpotLight>(null);

  // Forward the ref to parent
  useImperativeHandle(ref, () => localRef.current!, []);

  // In preview mode, use simplified transform
  const position = isPreview ? [0, 4, 0] : [transform.position.x, transform.position.y, transform.position.z];
  const rotation = isPreview ? [-Math.PI / 2, 0, 0] : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  
  // Calculate target position based on rotation
  const targetPosition = isPreview 
    ? [0, 0, 0] 
    : [
        transform.position.x - Math.sin(transform.rotation.y) * Math.cos(transform.rotation.x),
        transform.position.y - Math.sin(transform.rotation.x),
        transform.position.z - Math.cos(transform.rotation.y) * Math.cos(transform.rotation.x)
      ];

  return (
    <>
      <spotLight
        ref={localRef}
        intensity={intensity}
        color={color}
        position={position as [number, number, number]}
        angle={angle}
        penumbra={penumbra}
        distance={distance}
        decay={decay}
        castShadow
      >
        {!isPreview && (
          <mesh scale={0.2}>
            <sphereGeometry />
            <meshBasicMaterial color={color} />
          </mesh>
        )}
      </spotLight>
      
      {/* Target object for the spotlight */}
      <mesh 
        position={targetPosition as [number, number, number]} 
        scale={0.1}
        visible={!isPreview}
      >
        <sphereGeometry />
        <meshBasicMaterial color="#ffff00" />
      </mesh>
      
      {isPreview && (
        <>
          {/* Ground plane to show shadows */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]} receiveShadow>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color={colors.contrast} />
          </mesh>
          
          {/* Demo box to cast shadows */}
          <mesh position={[0, 0, 0]} castShadow receiveShadow>
            <boxGeometry args={[1, 1, 1]} />
            <meshStandardMaterial color={colors.primary} />
          </mesh>
          
          {/* Light indicator cone */}
          <mesh position={position as [number, number, number]} rotation={rotation as [number, number, number]}>
            <coneGeometry args={[0.5, 1, 16]} />
            <meshBasicMaterial color={color} transparent opacity={0.3} />
          </mesh>
        </>
      )}
    </>
  );
});

export const SpotLightItem: Light<SpotLightProperties> = {
  id: '',
  name: 'Spot Light',
  type: 'light',
  subtype: 'spot',
  icon: 'Flashlight',
  properties: {
    intensity: 1,
    color: '#ffffff',
    angle: Math.PI / 6, // 30 degrees in radians
    penumbra: 0.2,
    distance: 10,
    decay: 2,
    transform: {
      position: new Vector3(0, 3, 0),
      rotation: new Euler(-Math.PI / 2, 0, 0), // Pointing down by default
      scale: new Vector3(1, 1, 1),
    },
  },
  fields: [
    ...LightFields,
    {
      key: 'properties.angle',
      type: 'number',
      label: 'Angle',
      default: Math.PI / 6,
      min: 0,
      max: Math.PI / 2,
      step: 0.01,
      panel: 'light',
    },
    {
      key: 'properties.penumbra',
      type: 'number',
      label: 'Penumbra',
      default: 0.2,
      min: 0,
      max: 1,
      step: 0.01,
      panel: 'light',
    },
    {
      key: 'properties.distance',
      type: 'number',
      label: 'Distance',
      default: 10,
      min: 0,
      max: 100,
      step: 0.1,
      panel: 'light',
    },
    {
      key: 'properties.decay',
      type: 'number',
      label: 'Decay',
      default: 2,
      min: 0,
      max: 10,
      step: 0.1,
      panel: 'light',
    },
    {
      key: 'properties.transform.position',
      type: 'vector3',
      label: 'Position',
      default: new Vector3(0, 3, 0),
      panel: 'transform',
    },
    {
      key: 'properties.transform.rotation',
      type: 'euler',
      label: 'Rotation',
      default: new Euler(-Math.PI / 2, 0, 0),
      panel: 'transform',
    },
    {
      key: 'properties.transform.scale',
      type: 'vector3',
      label: 'Scale',
      default: new Vector3(1, 1, 1),
      panel: 'transform',
    },
  ],
  render: (properties: SpotLightProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <SpotLightComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.SpotLight>} />;
  }
}; 